import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy } from '@angular/core';

/** Минимальная ширина колонки, px. */
const MIN_WIDTH = 60;

/**
 * Ширину колонок таблицы пользователь меняет сам: тянет «ручку» у правого края заголовка.
 * Ширины запоминаются в браузере под ключом таблицы; двойной клик по ручке — вернуть исходную разметку.
 *
 *   <table class="pp-table" appResizableColumns="ff-deals">
 *
 * Последняя колонка (стрелка) не тянется. Таблица при расширении становится шире контейнера —
 * её обёртка .pp-table-wrap прокручивается по горизонтали.
 */
@Directive({
  selector: 'table[appResizableColumns]',
  standalone: true
})
export class ResizableColumnsDirective implements AfterViewInit, OnDestroy {
  /** Ключ таблицы для запоминания ширин. */
  @Input('appResizableColumns') key = '';

  private handles: HTMLElement[] = [];
  private stopDrag: (() => void) | null = null;

  constructor(
    private el: ElementRef<HTMLTableElement>,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    // Таблица может появиться позже (после загрузки) — тогда директива создаётся вместе с ней
    this.zone.runOutsideAngular(() => {
      this.addHandles();
      const saved = this.load();
      if (saved) this.apply(saved);
    });
  }

  ngOnDestroy(): void {
    this.stopDrag?.();
    this.handles.forEach(h => h.remove());
  }

  private get headers(): HTMLTableCellElement[] {
    return Array.from(this.el.nativeElement.querySelectorAll<HTMLTableCellElement>('thead tr:first-child > th'));
  }

  private addHandles(): void {
    const ths = this.headers;
    ths.slice(0, -1).forEach((th, index) => {
      if (getComputedStyle(th).position === 'static') th.style.position = 'relative';
      const handle = document.createElement('span');
      handle.className = 'col-resize';
      handle.setAttribute('aria-hidden', 'true');
      handle.title = 'Потяните, чтобы изменить ширину. Двойной клик — сбросить';
      handle.addEventListener('pointerdown', e => this.startDrag(e, index));
      handle.addEventListener('click', e => e.stopPropagation());
      handle.addEventListener('dblclick', e => {
        e.stopPropagation();
        this.reset();
      });
      th.appendChild(handle);
      this.handles.push(handle);
    });
  }

  private startDrag(event: PointerEvent, index: number): void {
    event.preventDefault();
    event.stopPropagation();
    // Фиксируем текущие ширины всех колонок в px, чтобы при перетаскивании двигалась только одна
    const widths = this.headers.map(th => th.getBoundingClientRect().width);
    this.apply(widths);
    const startX = event.clientX;
    const startWidth = widths[index];
    const handle = this.handles[index];
    handle.classList.add('is-active');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const move = (e: PointerEvent) => {
      widths[index] = Math.max(MIN_WIDTH, Math.round(startWidth + e.clientX - startX));
      this.apply(widths);
    };
    const up = () => {
      this.stopDrag?.();
      this.save(widths);
    };
    this.stopDrag = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      handle.classList.remove('is-active');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.stopDrag = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  /** Ширины в px: каждая колонка своей ширины, таблица — по сумме (но не уже контейнера). */
  private apply(widths: number[]): void {
    const ths = this.headers;
    if (widths.length !== ths.length) return;
    ths.forEach((th, i) => (th.style.width = widths[i] + 'px'));
    const table = this.el.nativeElement;
    table.style.tableLayout = 'fixed';
    table.style.width = widths.reduce((s, w) => s + w, 0) + 'px';
    table.style.minWidth = '100%';
  }

  private reset(): void {
    this.headers.forEach(th => (th.style.width = ''));
    const table = this.el.nativeElement;
    table.style.width = '';
    table.style.minWidth = '';
    try { localStorage.removeItem(this.storageKey); } catch { /* браузер без хранилища — просто не запоминаем */ }
  }

  private get storageKey(): string {
    return 'bilload.colw.' + this.key;
  }

  private load(): number[] | null {
    if (!this.key) return null;
    try {
      const raw = localStorage.getItem(this.storageKey);
      const widths = raw ? (JSON.parse(raw) as number[]) : null;
      return Array.isArray(widths) && widths.length === this.headers.length ? widths : null;
    } catch {
      return null;
    }
  }

  private save(widths: number[]): void {
    if (!this.key) return;
    try { localStorage.setItem(this.storageKey, JSON.stringify(widths.map(Math.round))); } catch { /* не запоминаем */ }
  }
}
